import axios, { AxiosError } from 'axios'
import { defaultRequestInterceptors, defaultResponseInterceptors } from './config'

import { AxiosInstance, InternalAxiosRequestConfig, RequestConfig, AxiosResponse } from './types'
import { ElMessage } from 'element-plus'
import { REQUEST_TIMEOUT } from '@/constants'

export const PATH_URL = import.meta.env.VITE_API_BASE_PATH

const abortControllerMap: Map<string, AbortController> = new Map()

const axiosInstance: AxiosInstance = axios.create({
  timeout: REQUEST_TIMEOUT,
  baseURL: PATH_URL
})

axiosInstance.interceptors.request.use((res: InternalAxiosRequestConfig) => {
  const controller = new AbortController()
  const url = res.url || ''
  res.signal = controller.signal
  abortControllerMap.set(
    import.meta.env.VITE_USE_MOCK === 'true' ? url.replace('/mock', '') : url,
    controller
  )
  return res
})

axiosInstance.interceptors.response.use(
  (res: AxiosResponse) => {
    const url = res.config.url || ''
    abortControllerMap.delete(url)
    // 這裡不能做任何處理，否則後面的 interceptors 拿不到完整的上下文了
    return res
  },
  (error: AxiosError) => {
    console.log('err： ' + error) // for debug
    if (error.response?.data) {
      ElMessage.closeAll()
      const errorData: any = error.response?.data
      // if (errorData.title === 'Forbidden' && errorData.status === 403) {
      //   ElMessage.error(`Error: 權限不足`)
      // } else {
      //   ElMessage.error(error.response?.data) // 顯示錯誤消息
      // }
      const status = error.response?.status
      if (
        status === 403 ||
        (errorData && errorData.title === 'Forbidden' && errorData.status === 403)
      ) {
        if (
          error.config?.url?.indexOf('/api/auth/login') !== -1 ||
          (error.request &&
            error.request.responseURL &&
            error.request.responseURL.indexOf('/api/auth/login') !== -1)
        ) {
          ElMessage.error(errorData?.message || `登入資訊錯誤`)
        } else {
          ElMessage.error(errorData?.message || `權限不足`)
        }
      } else {
        // ASP.NET Core ValidationProblemDetails：errors 為 { 欄位名: string[] } 的驗證錯誤字典
        const validationMessages =
          errorData?.errors && typeof errorData.errors === 'object'
            ? Object.values(errorData.errors)
                .flat()
                .filter((msg): msg is string => typeof msg === 'string' && msg.length > 0)
            : []

        ElMessage.error(
          validationMessages.join(' ') ||
            errorData?.message ||
            errorData?.detail ||
            errorData?.title ||
            error.message
        ) // 顯示錯誤消息
      }
    } else {
      ElMessage.error(error.message)
    }
    return Promise.reject(error)
  }
)

axiosInstance.interceptors.request.use(defaultRequestInterceptors)
axiosInstance.interceptors.response.use(defaultResponseInterceptors)

const service = {
  request: (config: RequestConfig) => {
    return new Promise((resolve, reject) => {
      if (config.interceptors?.requestInterceptors) {
        config = config.interceptors.requestInterceptors(config as any)
      }

      if (config.url?.includes('/mock/')) {
        config.baseURL = ''
      }

      axiosInstance
        .request(config)
        .then((res) => {
          resolve(res)
        })
        .catch((err: any) => {
          reject(err)
        })
    })
  },
  cancelRequest: (url: string | string[]) => {
    const urlList = Array.isArray(url) ? url : [url]
    for (const _url of urlList) {
      abortControllerMap.get(_url)?.abort()
      abortControllerMap.delete(_url)
    }
  },
  cancelAllRequest() {
    for (const [_, controller] of abortControllerMap) {
      controller.abort()
    }
    abortControllerMap.clear()
  }
}

export default service
